import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { X } from 'lucide-react'

/** Right-side slide-over panel. Flat, sharp, no gradient. */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  tone = 'light',
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  subtitle?: ReactNode
  children: ReactNode
  footer?: ReactNode
  /** 'brand' = dark header with a brand-red accent (matches the sidebar). */
  tone?: 'light' | 'brand'
}) {
  const brand = tone === 'brand'
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    if (open) document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-coal/40 backdrop-blur-[1px] animate-[fadeIn_.15s_ease]" onClick={onClose} />
      <aside className="relative flex h-full w-full max-w-lg flex-col border-l border-line bg-paper shadow-pop">
        <header
          className={
            brand
              ? 'flex items-start justify-between gap-4 border-b-2 border-[#E1021B] px-6 py-4'
              : 'flex items-start justify-between gap-4 border-b border-line bg-card px-6 py-4'
          }
          style={brand ? { background: 'linear-gradient(135deg,#1c1c1f 0%,#0e0e10 100%)' } : undefined}
        >
          <div className="min-w-0">
            <div className={brand ? 'text-[15px] font-bold tracking-tight text-white' : 'text-[15px] font-bold tracking-tight text-coal'}>
              {title}
            </div>
            {subtitle && (
              <div className={brand ? 'mt-1 text-[12.5px] text-[#9a9aa1]' : 'mt-1 text-[12.5px] text-muted'}>{subtitle}</div>
            )}
          </div>
          <button
            onClick={onClose}
            className={
              brand
                ? '-mr-1 rounded-lg p-1.5 text-[#9a9aa1] transition-colors hover:bg-white/10 hover:text-white'
                : '-mr-1 rounded-lg p-1.5 text-muted transition-colors hover:bg-paper hover:text-coal'
            }
            aria-label="Chiudi"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && <div className="border-t border-line bg-card px-6 py-3.5">{footer}</div>}
      </aside>
    </div>
  )
}
